'use client';
import React, { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { usePathname } from '@/navigation';

import {
  TypeMediaTextTitle,
  TypeProduct,
  TypeProductFields,
} from '@/core/generated-types';
import {
  ShopifyProduct,
  ShopifyVariant,
  ShopifyProductType,
  getProductInventoryStatus,
} from '@/lib/shopify';
import { captureProductFieldsEvent, pushEvent } from '@/utils/dataLayer';
import useProductContext from '@/context/ProductContext';
import { DynamicComponent, HeaderText, TrustedShopReviews } from '@/components';
import { Image } from '@/components/ui/Image';
import { HeaderSize } from '@/components/ui/HeaderText';
import { YotpoReviewStars } from '@/components/ui/YotpoReviewStars';
import { InventoryStatus, AddButton } from '../InventoryStatus';
import { ProductOptionsRadio } from '.';
import {
  shouldUseYotpoReviews,
  shouldUseTrustedShopsReviews,
} from '@/utils/helpers';
import { API_URLS } from '@/lib/api/config';
import { PRODUCT_EVENT_TYPES } from '@/utils/rudderstack';
import { getDefaultVariant } from '@/utils/shopify';
import { ITEM_ORIGINS } from '@/core/constants';

export interface ProductOptionsAddToCartProps {
  products: TypeProduct[];
  shopifyProducts: ShopifyProduct[];
}

const handleFormSubmit = (
  e: React.FormEvent<HTMLFormElement>,
  shopifyProduct: ShopifyProduct,
  variant: ShopifyVariant,
  productFields: TypeProductFields,
  url: string,
  defaultQuantity: number,
) => {
  try {
    e.preventDefault();

    const qtyVal = defaultQuantity;

    pushEvent('addToCart', { qtyVal, shopifyProduct, variant });
    captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_ADDED, {
      shopifyProduct,
      variant,
      product: productFields,
      quantity: qtyVal,
      url,
    });

    e.currentTarget.submit();
  } catch (error) {
    console.error('Error tracking ATC event', error);
    e.currentTarget.submit();
  }
};

const ProductOptionsAddToCart: FC<ProductOptionsAddToCartProps> = ({
  products,
  shopifyProducts,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const variantUrlParams = searchParams?.get('variant');
  const { enableYotpoReviewsSummary, enableTrustedShopsReviewsSummary } =
    useProductContext();

  // The component has a default option selected on the page load,
  // so the first index is automatically used as the main product
  const product = products[0];
  const shopifyProduct = shopifyProducts[0];

  const isSkillSet =
    shopifyProduct?.productType === ShopifyProductType.SKILL_SET;

  const [productSelected, setProductSelected] = useState(product);
  const [shopifyProductSelected, setShopifyProductSelected] =
    useState(shopifyProduct);

  const productFields = product.fields;
  const { variants } = shopifyProductSelected as ShopifyProduct;
  const defaultVariant = getDefaultVariant(productFields.title, variants);
  const [variant, setVariant] = useState<ShopifyVariant>(defaultVariant);

  const qtyVal = 1;

  const handleOptionSelected = (
    product: TypeProduct,
    shopifyProduct: ShopifyProduct,
    variant: ShopifyVariant,
  ) => {
    setProductSelected(product);
    setShopifyProductSelected(shopifyProduct);
    setVariant(variant);
  };

  useEffect(() => {
    if (shopifyProduct) {
      const urlVariant: ShopifyVariant | undefined = variants?.find(
        (variant) =>
          `gid://shopify/ProductVariant/${variantUrlParams ?? ''}` ==
          variant.id,
      );

      if (urlVariant) {
        setVariant(urlVariant);
        pushEvent('productView', {
          qtyVal,
          shopifyProduct,
          variant: urlVariant,
        });
        captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_VIEWED, {
          shopifyProduct,
          variant: urlVariant,
          product: productFields,
          quantity: qtyVal,
          url: pathname || '',
        });
        return;
      }

      // no variant selected, use the default selected variant
      if (variants && variants.length) {
        setVariant(defaultVariant);
        pushEvent('productView', {
          qtyVal: 1,
          shopifyProduct: shopifyProductSelected,
          variant: defaultVariant,
        });
        captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_VIEWED, {
          shopifyProduct: shopifyProductSelected,
          variant: defaultVariant,
          product: productFields,
          quantity: qtyVal,
          url: pathname || '',
        });
        return;
      }

      console.warn('Product is missing variants');
    }
    // Removed dependency array to avoid triggering multiple events
    // when component is re-rendered due to options selection
  }, []);

  const inventoryStatus = getProductInventoryStatus(
    shopifyProductSelected,
    variant,
  );

  return (
    <div className="flex flex-col sm:px-6">
      <div className="flex items-center mb-2 md:mb-3">
        {productFields.previousTitle && (
          <span className="mr-4 text-h2 font-bold">
            {productFields.previousTitle}
          </span>
        )}
        {productFields.badge && (
          <div>
            <DynamicComponent component={productFields.badge} />
          </div>
        )}
      </div>
      <HeaderText
        text={productFields.title}
        size={HeaderSize.H1}
        classes="mb-1.5"
      />
      {productFields.shortDescription && (
        <p className="mb-[9px]">{productFields.shortDescription}</p>
      )}
      {productFields.additionalDescription && (
        <p className="pt-3 mb-[9px]">{productFields.additionalDescription}</p>
      )}
      {variant && (
        <p className="mb-3 text-[1.3rem] font-bold md:text-2xl hidden">
          {productFields.precedingPriceText && (
            <span>{productFields.precedingPriceText}</span>
          )}
          {variant.formattedPrice}
        </p>
      )}
      {shouldUseYotpoReviews() && enableYotpoReviewsSummary && (
        <div className="mb-5 md:mb-6 mt-1 md:mt-2">
          <YotpoReviewStars product={product} />
        </div>
      )}
      {shouldUseTrustedShopsReviews() && enableTrustedShopsReviewsSummary && (
        <div className="mb-2">
          <TrustedShopReviews
            shopifyProduct={shopifyProduct}
            widgetType="ratings-short"
          />
        </div>
      )}
      <ProductOptionsRadio
        products={products}
        productSelected={productSelected}
        shopifyProducts={shopifyProducts}
        onChange={handleOptionSelected}
      />
      {shopifyProductSelected && (
        <form
          id="add-to-cart-form"
          action={`${API_URLS.CART_SERVICE}/add`}
          method="post"
          onSubmit={(e) =>
            handleFormSubmit(
              e,
              shopifyProductSelected,
              variant,
              productSelected.fields,
              pathname || '',
              qtyVal,
            )
          }
        >
          <input
            type="hidden"
            name="lines"
            value={JSON.stringify([
              {
                merchandiseId: variant.id,
                quantity: qtyVal,
                attributes: [
                  ...(isSkillSet &&
                  shopifyProductSelected.handle !== 'reading-skill-set'
                    ? [
                        {
                          key: 'linked_product_handle',
                          value: 'reading-skill-set',
                        },
                      ]
                    : []),
                  {
                    key: 'quantity_limited_to_one',
                    value: String(isSkillSet),
                  },
                  {
                    key: 'is_skill_set',
                    value: String(isSkillSet),
                  },
                  {
                    key: 'item_origin',
                    value: ITEM_ORIGINS.PRODUCT_PAGE,
                  },
                ],
              },
            ])}
          />
          <div className="text-sm">
            <InventoryStatus
              inventoryStatus={inventoryStatus}
              product={product}
            />
          </div>
          {productFields.backOrderMessage && (
            <p className="mt-1 mb-3">{productFields.backOrderMessage}</p>
          )}
          <div>
            <AddButton inventoryStatus={inventoryStatus} product={product} />
          </div>
        </form>
      )}
      <ul className="mt-4 mb-4">
        {productFields.productValueProps?.map((item) => {
          const {
            sys: { id },
            fields: { textHeading, image },
          } = item as TypeMediaTextTitle;

          return (
            <li
              key={id}
              className="flex-start flex flex-row items-center gap-2"
            >
              {image && (
                <Image imageData={image} height={16} width={16} alt="" />
              )}
              <p className="w-auto">{textHeading}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ProductOptionsAddToCart;
